
import { z } from "zod";

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB
const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png"];

export const teamLeaderSchema = z.object({
    user_name: z.string().min(1, "Name is required"),
    email: z.string().email("Invalid email address"),
    password: z.string().min(8, "Password must be at least 8 characters"),
    password_confirmation: z.string(),
    phone: z.string().max(20, "Phone number too long").optional(),
    avatar: z
        .any()
        .optional()
        .refine((file) => {
            if (!file) return true;
            if (file instanceof File) {
                return file.size <= MAX_FILE_SIZE;
            }
            return true;
        }, `Max file size is 2MB.`)
        .refine((file) => {
            if (!file) return true;
            if (file instanceof File) {
                return ACCEPTED_IMAGE_TYPES.includes(file.type);
            }
            return true;
        }, "Only .jpg, .jpeg, .png formats are supported."),
    birth_date: z.coerce.date().refine((date) => {
        const today = new Date();
        const age = today.getFullYear() - date.getFullYear();
        return age >= 18; // Simple check, exact date check can be more complex if needed
    }, "Must be at least 18 years old"),
    gender: z.coerce.number().int().refine((val) => [1, 2, 3].includes(val), "Invalid gender selection"), // 1: Male, 2: Female, 3: Other
}).refine((data) => data.password === data.password_confirmation, {
    message: "Passwords don't match",
    path: ["password_confirmation"],
});

export const teamDataSchema = z.object({
    name: z.string().min(1, "Team Name is required").max(200, "Name too long"),
    city: z.string().max(100, "City name too long").optional(),
    address: z.string().optional(),
    logo_url: z.string().url("Invalid URL").optional().or(z.literal('')),
    monthly_target: z.coerce.number().min(0, "Target must be positive"),
    max_agents: z.coerce.number().min(1, "At least 1 agent required"),
    subscription_plan: z.enum(["basic", "premium", "enterprise"]),
    monthly_fee: z.coerce.number().min(0, "Fee must be positive"),
    subscription_ends_at: z.coerce.date().optional().refine((date) => {
        if (!date) return true;
        return date >= new Date(new Date().setHours(0, 0, 0, 0));
    }, "Date must be in the future"),
    status: z.enum(["active", "suspended", "inactive"]),
});

// Keys for each step to use with trigger
export const teamLeaderKeys = ["user_name", "email", "password", "password_confirmation", "phone", "avatar", "birth_date", "gender"];
export const teamDataKeys = ["name", "city", "address", "logo_url", "monthly_target", "max_agents", "subscription_plan", "monthly_fee", "subscription_ends_at", "status"];

// Combined schema (if needed for final submit, though we might submit parts)
export const newTeamSchema = z.intersection(teamLeaderSchema, teamDataSchema);
